import { makeNewId } from "common/ids";
import * as claims from "domain/claims";
import * as attatchmentActionTypes from "store/actions/input/attachments/attachmentsActionTypes";
import * as actionTypes from "store/actions/input/claims/claimsActionTypes";
import * as actions from "store/actions/input/claims/claimsActions";
import * as persistenceActionTypes from "store/actions/persistence/persistenceActionTypes";

export const INITIAL_STATE = {
  files: [],
  activeFile: null,
  asAtDate: null,
  recommendedAsAtDate: null,
  manualClaims: [],
  threshold: 10000,
  deltaThreshold: 100000,
  inflationModel: "UNEXPECTEDLY_THE_INFLATION_MODEL_HAS_NOT_BEEN_SET",
  duplicateGroups: [],
  ignoredDuplicates: [],
  acceptedDuplicates: [],
  manualClaimsMatches: [],
  aiClaimsMatches: [],
  reportedLossOverrides: {},
};

const getManualClaimsKey = (actionType) => {
  switch (actionType) {
    case actionTypes.MANUAL_UPDATE_LOSS_YEAR:
      return "lossYear";
    case actionTypes.MANUAL_UPDATE_COVERAGE:
      return "coverage";
    case actionTypes.MANUAL_UPDATE_LOSS:
      return "lossAmount";
    case actionTypes.MANUAL_UPDATE_DESCRIPTION:
      return "description";
    case actionTypes.MANUAL_UPDATE_CLAIMANT:
      return "claimant";
    case actionTypes.MANUAL_UPDATE_OPEN_CLOSED:
      return "oc";
    case actionTypes.MANUAL_UPDATE_DATE_CLOSED:
      return "dateClosed";
    default:
      throw new Error(`${actionType} is an Unsupported Claim Detail`);
  }
};

const updateFiles = (files, key, update) =>
  (files || []).map((file) =>
    file.claimsKey === key ? { ...file, ...update } : file
  );

const deriveRetentionKey = (r) =>
  r?.retentionKey ??
  (r?.retentionType && r?.retentionCoverageCategory
    ? `${r.retentionType}-${r.retentionCoverageCategory}`
    : undefined);

const applyRetentionPatch = (prev, patch) => {
  const next = { ...prev, ...patch };

  if (
    (!next.retentionCoverageCategory ||
      next.retentionCoverageCategory === "") &&
    next.lossType
  ) {
    next.retentionCoverageCategory = "OTHER";
    next.origin = next.origin ?? "imported";
    next.importedFromLossType = next.importedFromLossType ?? next.lossType;

    if (!next.retentionType) {
      next.retentionType = "SIMPLE";
    }
  }

  if (!next.retentionKey) {
    const k = deriveRetentionKey(next);
    if (k) next.retentionKey = k;
  }

  if (next.id == null || next.id === "") {
    next.id = prev?.id ?? makeNewId();
  }

  const origin = next.origin ?? prev?.origin ?? undefined;
  if (origin === "default") {
    const lastDefaultValue = patch.hasOwnProperty("lastDefaultValue")
      ? patch.lastDefaultValue
      : prev?.lastDefaultValue ?? null;
    if (patch.hasOwnProperty("value")) {
      next.userModified = next.value !== lastDefaultValue;
    } else if (patch.hasOwnProperty("lastDefaultValue")) {
      next.userModified = next.value !== next.lastDefaultValue;
    }
  }
  return next;
};

const claimsReducer = (state, action) => {
  if (state === undefined) {
    state = INITIAL_STATE;
  }

  switch (action.type) {
    case persistenceActionTypes.LOAD_SUBMISSION_STARTED:
      return {
        ...INITIAL_STATE,
      };
    case persistenceActionTypes.LOAD_SUBMISSION_SUCCEEDED: {
      const loaded = action?.payload?.data?.state?.input?.claims || {};
      const normalizedSir = (loaded.selfInsuredRetentions || []).map((r) =>
        applyRetentionPatch(r, {})
      );

      // Migration: Convert old Set objects to arrays (Sets aren't serializable in Redux)
      let ignoredDuplicates = loaded.ignoredDuplicates || [];
      if (!Array.isArray(ignoredDuplicates)) {
        // Convert Set-like object or any other type to array
        try {
          ignoredDuplicates = Array.from(ignoredDuplicates);
        } catch {
          ignoredDuplicates = [];
        }
      }

      return {
        ...INITIAL_STATE,
        ...loaded,
        selfInsuredRetentions: normalizedSir,
        ignoredDuplicates,
      };
    }
    case actionTypes.CLEAR_CLAIMS_FILTER:
      return { ...state, filter: {} };
    case actionTypes.UPDATE_CLAIMS_FILTER:
      return {
        ...state,
        filter: {
          ...(state.filter || {}),
          [action.payload.filter]: action.payload.value,
        },
      };
    case actionTypes.UPDATE_CLAIM_THRESHOLD:
      return {
        ...state,
        threshold: action.payload,
      };
    case actionTypes.UPDATE_DELTA_CLAIM_THRESHOLD:
      return {
        ...state,
        deltaThreshold: action.payload,
      };
    case attatchmentActionTypes.COPYING_ATTACHMENT_TO_LOSS_FILE:
      return {
        ...state,
        activeFile: {
          fileIsUploading: true,
          sheetsDialogIsOpen: true,
          attachment: action.payload,
          file: action.payload.filename,
          step: 0,
        },
        fileUploadCancelled: false,
      };
    case actionTypes.SET_V2_CLAIMS_KEY:
      return {
        ...state,
        files: [
          ...(state.files ?? []),
          {
            sheets: action.payload.tablesList,
            activeSheet: action.payload.tablesList.indexOf(
              action.payload.tableName
            ),
            sheetName: action.payload.tableName,
            checked: true,
            prettyName: action.payload.uploadedFilename,
            originalDownloadHasError: false,
            originalDownloading: false,
            ...action.payload,
            isFilesFile: true,
            fileBackendV2: true,

            originalFileId: action.payload.originalFileId,
            originalFilename: action.payload.originalFilename,
            extractionDetails: action.payload.extractionDetails,
          },
        ],
      };
    case actionTypes.DOWNLOAD_ORIGINAL_CLAIMS_DATA_STARTED:
      return {
        ...state,
        files: updateFiles(state.files, action.payload, {
          originalDownloading: true,
        }),
      };
    case actionTypes.DOWNLOAD_ORIGINAL_CLAIMS_DATA_RETURNED:
      return {
        ...state,
        files: updateFiles(state.files, action.payload, {
          originalDownloadHasError: false,
          originalDownloading: false,
        }),
      };
    case actionTypes.DOWNLOAD_ORIGINAL_CLAIMS_DATA_FAILED:
      return {
        ...state,
        files: updateFiles(state.files, action.payload, {
          originalDownloading: false,
          originalDownloadHasError: true,
        }),
      };
    case actionTypes.CLAIMS_FILE_CHECKED:
      return {
        ...state,
        files: updateFiles(state.files, action.payload.key, {
          checked: action.payload.checked,
        }),
      };
    case actionTypes.UPDATE_CLAIMS_NOTE:
      return {
        ...state,
        note: action.payload,
      };
    case actionTypes.UPDATE_CLAIMS_FIELDS:
      return {
        ...state,
        fields: { ...state.fields, ...action.payload },
      };
    case actionTypes.TOGGLE_CLAIM_EXCLUSION:
      const toggleClaimExclusion = (list) => {
        if (action.payload.excluded) {
          if (list.includes(action.payload.value)) {
            return list;
          }
          return [...list, action.payload.index];
        }
        return list.filter((x) => x !== action.payload.index);
      };
      return {
        ...state,
        exclusions: {
          ...state.exclusions,
          [action.payload.key]: toggleClaimExclusion(
            state.exclusions?.[action.payload.key] || []
          ),
        },
      };
    case actionTypes.OVERIDE_CLAIM_VALUE:
      const updateOverride = (overrides) => {
        if (action.payload.value != null) {
          return { ...overrides, [action.payload.index]: action.payload.value };
        }
        const { [action.payload.index]: value, ...without } = overrides;
        return without;
      };
      return {
        ...state,
        overrides: {
          ...state.overrides,
          [action.payload.key]: updateOverride(
            state.overrides?.[action.payload.key] || {}
          ),
        },
      };
    case actionTypes.OVERRIDE_REPORTED_LOSS_CLAIM_VALUE:
      const updateReportedLossOverride = (overrides) => {
        if (action.payload.value != null) {
          return { ...overrides, [action.payload.index]: action.payload.value };
        }
        const { [action.payload.index]: value, ...without } = overrides;
        return without;
      };
      return {
        ...state,
        reportedLossOverrides: {
          ...state.reportedLossOverrides,
          [action.payload.key]: updateReportedLossOverride(
            state.reportedLossOverrides?.[action.payload.key] || {}
          ),
        },
      };
    case actionTypes.SET_CLAIM_OPEN_CLOSED_OVERRIDE:
      return {
        ...state,
        openClosedOverrides: {
          ...state.openClosedOverrides,
          [action.payload.key]: {
            ...state.openClosedOverrides?.[action.payload.key],
            [action.payload.index]: action.payload.value,
          },
        },
      };
    case actionTypes.REMOVE_CLAIM_OPEN_CLOSED_OVERRIDE:
      return {
        ...state,
        openClosedOverrides: {
          ...state.openClosedOverrides,
          [action.payload.key]: Object.fromEntries(
            Object.entries(
              state.openClosedOverrides?.[action.payload.key] ?? {}
            ).filter(
              ([index]) => String(index) !== String(action.payload.index)
            )
          ),
        },
      };
    case actionTypes.MANUAL_CREATE_MERGE_ENTRY:
      const curClaims = state.manualClaims || [];
      action.payload.index = curClaims.length;
      const newState = {
        ...state,
        manualClaims: [...curClaims, action.payload],
      };
      return newState;

    case actionTypes.MANUAL_UPDATE_LOSS_YEAR:
    case actionTypes.MANUAL_UPDATE_OPEN_CLOSED:
    case actionTypes.MANUAL_UPDATE_DATE_CLOSED:
    case actionTypes.MANUAL_UPDATE_CLAIMANT:
    case actionTypes.MANUAL_UPDATE_DESCRIPTION:
    case actionTypes.MANUAL_UPDATE_LOSS:
    case actionTypes.MANUAL_UPDATE_COVERAGE:
      const oldManualClaims = state.manualClaims || [];
      const key = getManualClaimsKey(action.type);
      return {
        ...state,
        manualClaims: [
          ...oldManualClaims.slice(0, action.payload.index),
          {
            ...(oldManualClaims[action.payload.index] ??
              claims.DEFAULT_MANUAL_CLAIMS),
            [key]: action.payload[key],
          },
          ...oldManualClaims.slice(action.payload.index + 1),
        ],
      };
    case actionTypes.DELETE_MANUAL_CLAIMS:
      return {
        ...state,
        manualClaims: [
          ...(state.manualClaims || []).slice(0, action.payload.index),
          ...(state.manualClaims || []).slice(action.payload.index + 1),
        ],
      };
    case actionTypes.UPDATE_SELF_INSURED_RETENTION: {
      const list = state.selfInsuredRetentions || [];
      const patch = action.payload?.retention ?? {};
      const patchId = patch?.id;
      if (patchId == null) return state;
      const idx = list.findIndex((r) => String(r?.id) === String(patchId));
      if (idx >= 0) {
        const prev = list[idx] || {};
        const next = applyRetentionPatch(prev, patch);
        return {
          ...state,
          selfInsuredRetentions: [
            ...list.slice(0, idx),
            next,
            ...list.slice(idx + 1),
          ],
        };
      }
      const next = applyRetentionPatch({ id: patchId }, patch);
      return {
        ...state,
        selfInsuredRetentions: [...list, next],
      };
    }
    case actionTypes.DELETE_SELF_INSURED_RETENTION: {
      const list = state.selfInsuredRetentions || [];
      const { id } = action.payload || {};
      if (id == null) return state;
      return {
        ...state,
        selfInsuredRetentions: list.filter((r) => String(r?.id) !== String(id)),
      };
    }
    case actionTypes.SET_SELF_INSURED_RETENTIONS:
      return {
        ...state,
        selfInsuredRetentions: [
          ...((action.payload || []).map((r) => applyRetentionPatch(r, {})) ||
            []),
        ],
      };
    case actionTypes.CLAIMS_TABLE_ADD_LOSS_YEAR:
      return {
        ...state,
        lossYears: [...new Set([...(state.lossYears || []), action.payload])],
      };
    case actionTypes.CLAIMS_TABLE_REMOVE_LOSS_YEAR:
      return {
        ...state,
        lossYears: (state.lossYears || []).filter(
          (year) => year !== action.payload
        ),
      };
    case actionTypes.HIDE_CLAIMS_FILE:
      return {
        ...state,
        files: (state.files || []).filter(
          (file) => file.claimsKey !== action.payload
        ),
        hiddenFiles: [
          ...(state.hiddenFiles || []),
          ...(state.files || []).filter(
            (file) => file.claimsKey === action.payload
          ),
        ],
      };
    case actionTypes.SET_INFLATION_MODEL:
      return {
        ...state,
        inflationModel: action.payload,
      };
    case actions.updateTotalLossNumberFormat.toString():
      if (state.activeFile) {
        return {
          ...state,
          activeFile: {
            ...state.activeFile,
            totalLossNumberFormat: action.payload,
          },
        };
      } else {
        return state;
      }
    case actionTypes.UPDATE_AGGREGATED_CLAIMS:
      return {
        ...state,
        aggregatedClaims: {
          ...state.aggregatedClaims,
          ...action.payload,
        },
      };
    case actionTypes.UPDATE_AI_CLAIMS_MATCHES:
      return {
        ...state,
        aiClaimsMatches: action.payload,
      };
    case actionTypes.UPDATE_AI_MATCHING_DATA:
      return {
        ...state,
        aiMatchingResults: action.payload.results,
        aiMatchingPriorClaims: action.payload.priorClaims,
        aiMatchingCurrentClaims: action.payload.currentClaims,
      };
    case actionTypes.UPDATE_MANUAL_CLAIMS_MATCHES:
      return {
        ...state,
        manualClaimsMatches: action.payload,
      };
    case actionTypes.SET_DUPLICATE_GROUPS:
      return {
        ...state,
        duplicateGroups: action.payload,
      };
    case actionTypes.SET_IGNORED_DUPLICATES:
      return {
        ...state,
        ignoredDuplicates: action.payload,
      };
    case actionTypes.UPDATE_ACCEPTED_DUPLICATES:
      return {
        ...state,
        acceptedDuplicates: action.payload,
      };
    default:
      return state;
  }
};

export default claimsReducer;
